import { MoneyDisplay } from "@/ds/custom/MoneyDisplay";
import { getPriceAlertDescriptionType } from "@/features/priceAlerts/getPriceAlertDescriptionType";
import type { PriceAlertMetric } from "@/features/priceAlerts/PriceAlertMetric";
import { PriceAlertStepLabels, type PriceAlertStep } from "@/features/priceAlerts/PriceAlertStep";
import type { Currency } from "@/lib/domain/Currency";
import { PriceSourceLabels, type PriceSource } from "@/lib/domain/PriceSource";
import type { FC } from "react";

export interface PriceAlertDescriptionProps {
	targetPhrase: string;
	currentValue: number | null;
	metricType: PriceAlertMetric;
	metric: number;
	currency: Currency;
	step: PriceAlertStep | null;
	priceSource: PriceSource;
}

export const PriceAlertDescription: FC<PriceAlertDescriptionProps> = props => {
	const {
		targetPhrase,
		currentValue,
		metricType,
		metric,
		currency,
		step,
		priceSource
	} = props;

	const view = getPriceAlertDescriptionType(metricType, metric, currentValue);
	const stepPhrase = step ? `within ${PriceAlertStepLabels[step]}` : "since it was created";
	const priceSourceLabel = PriceSourceLabels[priceSource];

	switch (view.type) {
	case "invalidTarget":
		return (
			<div>
				The target value must be higher or lower than the current value.
			</div>
		);
	case "beforeCreation":
		return (
			<div>
				This price alert will trigger when the price of {targetPhrase} crosses <MoneyDisplay className="inline" money={{ value: metric, currency }} mode="Standard" preferredCurrency={currency} /> for the {priceSourceLabel} source.
			</div>
		);
	case "fixedValueThreshold":
		return (
			<div>
				This price alert will trigger when the price of {targetPhrase} {view.direction} to <MoneyDisplay className="inline" money={{ value: metric, currency }} mode="Standard" preferredCurrency={currency} /> {view.comparator} for the {priceSourceLabel} source.
			</div>
		);
	case "flatChange":
		return (
			<div>
				This price alert will trigger when the price of {targetPhrase} {view.direction} by <MoneyDisplay className="inline" money={{ value: metric, currency }} mode="Standard" preferredCurrency={currency} /> or more {stepPhrase} for the {priceSourceLabel} source.
			</div>
		);
	case "percentageChange":
		return (
			<div>
				This price alert will trigger when the price of {targetPhrase} {view.direction} by {metric}% or more {stepPhrase} for the {priceSourceLabel} source.
			</div>
		);
	}
};
